import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  Box,  
  CircularProgress, 
  Typography, 
  Tooltip, 
  IconButton, 
} from '@mui/material';
import {
  AddCircleOutline,
  Delete,
  DeleteForever
} from '@mui/icons-material';
import { DataGrid } from "@mui/x-data-grid";
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';


const Contacts = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const theme = useTheme();
  const primaryDark = theme.palette.primary.dark;
  const greenDeep = theme.palette.green.deep;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);  
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'single' or 'bulk'
  const [idToDelete, setIdToDelete] = useState(null);
  const [contacts, setContacts] = useState([]);


  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchContacts = async () => {
      try {
        setLoading(true);  
        const res = await api.get('/contacts');
        
        isMounted && setContacts(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchContacts();

    return () => {
      isMounted = false;
      controller.abort();
    }
  }, []); 


  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    },
    { 
      field: "fullName", 
      headerName: "FullName", 
      ...(() => {
        return isMobile ? { width: 200 } : { flex: 2 };
      })(),
    },
    {
      field: "email",
      headerName: "Email",
      ...(() => {
        return isMobile ? { width: 200 } : { flex: 2 };
      })(), 
    },
    { 
      field: "message", 
      headerName: "Message", 
      ...(() => {
        return isMobile ? { width: 300 } : { flex: 3 };
      })(), 
    },    
    {
      field: "status",
      headerName: "Status",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1.2 };
      })(),
    },
  ], [isMobile]);

  const hiddenColumns = {  
    _id: false,    
  };

  const nonMobileHiddenColumns = {
    _id: false,
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      filterable: false,      
      sortable: false,
      width: 80,      
      renderCell: (params) => {
        return (
          <FlexCenter
            gap={1}
            sx={{ width: '100%', height: '100%' }}
          >            
            {/* <Tooltip title="Edit">
              <IconButton
                aria-label="Edit"
                sx={{
                  color: primaryDark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': {
                    color: greenDeep,
                  },
                }}
                onClick={() => navigate(`/contacts/${params.row._id}`)}
              >
                <Edit />
              </IconButton>
            </Tooltip> */}            
            
            <Tooltip title="Delete">
              <IconButton
                aria-label="Delete"
                sx={{
                  color: primaryDark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': {
                    color: greenDeep,
                  },
                }}
                onClick={() => handleDeleteClick(params.row._id)}
              >
                <DeleteForever />
              </IconButton>                
            </Tooltip>           
          </FlexCenter>
        );
      },
    },
  ];

  const gridStyles = {
    "& .MuiDataGrid-row.isActive-false": {
      backgroundColor: `${theme.palette.primary.light} !important`,  
    },
  };
  
  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, contacts);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {

      if (deleteType === 'single') {
        if (!idToDelete) return;
        
        await api.delete(`/contacts/delete/${idToDelete}`);

        setContacts(contacts.filter((item) => item._id !== idToDelete));
        toast.success('Contact deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, contacts);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }

        const url = '/contacts/soft-delete';
        const payload = { ids: selectedIds };        
        const res = await api.patch(url, payload);        
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        
        setContacts(contacts.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err?.message || "Failed to delete!");
    }
  }, [deleteType, idToDelete, rowSelectionModel, contacts]);
 

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Contacts" 
          subtitle={`${loading ? 'Loading...' : 'List of Contacts'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>            
            <Tooltip title="Bulk soft delete" arrow>
              <span>
                <StyledButton 
                  disabled={!isAnyRowSelected(rowSelectionModel, contacts)}
                  onClick={handleBulkDelete}
                >
                  <Delete />
                </StyledButton>
              </span>
            </Tooltip>

            <Tooltip title="Create" arrow>
              <StyledButton 
                onClick={() => navigate("/contact")}
              >
                <AddCircleOutline />
              </StyledButton>
            </Tooltip>
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
          sx={{
            "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
          }}
        >
          <Box
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12" }}
          >
            {isMobile ? (
              <CardList
                rows={contacts}
                primaryField="fullName"
                secondaryField="email"
                fields={[
                  { label: "Message", key: "message" },
                  { label: "Status", key: "status" },
                ]}
                isActiveField={null}
                canEdit={false}
                canDelete={true}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGrid
                getRowId={(row) => row._id}
                rows={contacts || []}
                columns={columns.concat(actionColumn)}
                pageSizeOptions={[25, 50, 100]}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 100 },
                  },
                  columns: {
                    columnVisibilityModel: !isDesktop ? hiddenColumns : nonMobileHiddenColumns,
                  },
                }}
                checkboxSelection
                disableRowSelectionOnClick
                loading={loading}
                showToolbar
                onRowSelectionModelChange={(newRowSelectionModel) => {
                  setRowSelectionModel(newRowSelectionModel);
                }}
                rowSelectionModel={rowSelectionModel}
                sx={gridStyles}
              />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default Contacts;